import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, GoogleLogo } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  
  // Support both URL param (?redirect=) and navigation state
  const params = new URLSearchParams(loc.search);
  const redirectParam = params.get("redirect");
  const fromState = loc.state?.from?.pathname;
  const safeRedirect = (path) => (path && path.startsWith("/") ? path : null);
  const from = safeRedirect(redirectParam) || fromState || "/dashboard";

  // Detect OAuth callback (Supabase redirects back with ?code=...&state=...)
  const isOAuthCallback = params.has("code") && params.has("state");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto-redirect if already logged in (handles OAuth return)
  useEffect(() => {
    if (user && user.id) {
      toast.success("Welcome back!");
      // Admins go to /admin, everyone else goes to the original redirect
      navigate(user.is_admin ? "/admin" : from, { replace: true });
    }
  }, [user, navigate, from]);

  // Show loading state during OAuth callback processing
  if (isOAuthCallback) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white animate-pulse"
               style={{ background: "var(--primary)", border: "1.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}>
            RA
          </div>
          <p className="text-sm text-[var(--ink-muted)]">Signing you in…</p>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      // Admins go to /admin, everyone else goes to the original redirect
      const target = res.user?.is_admin ? "/admin" : from;
      navigate(target, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const handleGoogleSignIn = async () => {
    const res = await signInWithGoogle();
    if (!res.ok) {
      toast.error(res.error);
    }
    // OAuth redirects the browser — onAuthStateChange handles the rest
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white"
               style={{ background: "var(--primary)", border: "1.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}>
            RA
          </div>
          <span className="display font-black text-2xl">Rating Ally</span>
        </Link>

        <div className="card-pop p-7 sm:p-9">
          <h1 className="display text-3xl font-black tracking-tighter">Welcome back</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1">Sign in to manage your outlets.</p>

          {/* Google OAuth */}
          <button
            onClick={handleGoogleSignIn}
            className="btn-pop w-full justify-center mt-6 flex items-center gap-2"
            type="button"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: "var(--border)" }}></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-[var(--ink-muted)]">or</span>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                className="input-pop mt-1.5"
                placeholder="you@cafe.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Password</label>
                <Link to="/forgot-password" className="text-xs font-medium text-[var(--primary)] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                data-testid="login-password-input"
                type="password"
                required
                className="input-pop mt-1.5"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button data-testid="login-submit-btn" disabled={loading} type="submit" className="btn-primary w-full justify-center mt-2">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} weight="bold" /></>)}
            </button>
          </form>

          <div className="mt-6 text-sm text-center text-[var(--ink-muted)]">
            New here?{" "}
            <Link to="/register" data-testid="login-go-register-link" className="font-bold text-[var(--ink)] underline">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}